import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';

import { CalculatePercentagePipe } from '../../../pipes/calculate-percentage/calculate-percentage.pipe';
import { ToastNotificationsManagerService } from '../../../services/toast-notifications-manager/toast-notifications-manager.service';
import { ToastNotif } from '../../../types/toast-notification.type';
import { Timer } from '../../../utils/timeout-expanded/timer';

/**ToastNotificationHostComponent serves as a container that does not break the
 * page height or layout and will display notifications as toasts that hover above
 * the remaining page content and stack on top of each other.
 * The
 */
@Component({
  selector: 'acp-toast-notification-host, [acp-toast-notification-host]',
  imports: [CalculatePercentagePipe],
  templateUrl: './toast-notification-host.component.html',
  styles: [
    `
      .btn-close.with-timer {
        margin: 0.4375rem;
        position: relative;
      }

      .progress-ring {
        position: absolute;
        top: 5px;
        left: 5px;
        z-index: -1;
        transform: rotate(-90deg);
      }

      .progress-ring__circle {
        transition: stroke-dashoffset 0.1s linear;
      }
    `,
  ],
})
export class ToastNotificationHostComponent implements OnInit, OnDestroy {
  toasts: ToastNotif[] = [];

  mouseLeaveTimer: Timer | undefined = undefined;

  /**config for timer circle around close button */
  readonly radius = 14;
  readonly center = 16;
  readonly circumference = 2 * Math.PI * this.radius;

  private _onDestroy = new Subject<void>();

  constructor(
    private toastNotif: ToastNotificationsManagerService,
    private destroyRef: DestroyRef,
  ) {}

  /**bindToToastService() only subscribe to the toasts list and update the copy here */
  bindToToastService() {
    this.toastNotif.toastNotif$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(toasts => (this.toasts = toasts));
  }

  /**
   * killToast() call for a method to remove single notifcation
   * @param id is the toast id to target it
   */
  killToast(id: string) {
    this.toastNotif.removeNotif(id);
  }

  /**pauseAllToastTimers() happens when user hover over any toast - it makes
   * sense to pause all timers because otherwise the toast stack will keep
   * moving as notifications timers will run out and start removing the
   * notifications causing the one user wants to see to shift upwards. */
  pauseAllToastTimers() {
    if (this.mouseLeaveTimer) this.mouseLeaveTimer.terminate();
    this.toasts.forEach(toast => toast.hideTimer?.pause());
  }

  /**onMouseLeave() will start a new timer to resume all toast hide Timers.
   * The Timer here is to delay this hide Timer resume due to the small gap
   * between each toast. If user drags the mouse from one toast to the other,
   * for that small time toasts will start counting again. This timer provides
   * a delay that will only start after certain amount of time improving UX and
   * partially performance. */
  onMouseLeave() {
    if (this.mouseLeaveTimer) this.mouseLeaveTimer.terminate();
    this.mouseLeaveTimer = new Timer(this.resumeAllToastsTimers.bind(this), 500);
  }

  /**resumeAllToastsTimers() will iterate over toasts and resume Timers
   * if the toast has one. */
  resumeAllToastsTimers() {
    this.toasts.forEach(toast => toast.hideTimer?.resume());
  }

  ngOnInit(): void {
    this.bindToToastService();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
